import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, SlideshowSelectEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

const QUICK_ADD_PRODUCT_SECTION_ID = 'main';
const QUICK_ADD_MOBILE_TABLET_QUERY = '(max-width: 989px)';
const quickAddContentCache = new Map();
const quickAddPendingContent = new Map();
let quickAddViewportPrefetchObserver;
let quickAddRequestCounter = 0;

function isQuickAddMobileOrTabletBreakpoint() {
  return window.matchMedia(QUICK_ADD_MOBILE_TABLET_QUERY).matches;
}

function cancelCurrentQuickAddRequest() {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;

  modalContent.dataset.hpQuickAddRequest = '';
  modalContent.dataset.hpQuickAddClosedAt = String(Date.now());
}

function getQuickAddViewportPrefetchObserver() {
  if (!('IntersectionObserver' in window)) return null;

  quickAddViewportPrefetchObserver ??= new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        observer.unobserve(entry.target);

        if (entry.target instanceof QuickAddComponent) {
          window.setTimeout(() => entry.target.prefetchProductPage(), 0);
        }
      }
    },
    {
      rootMargin: '2200px 0px',
    }
  );

  return quickAddViewportPrefetchObserver;
}

/**
 * @param {QuickAddComponent} component
 */
function observeQuickAddViewportPrefetch(component) {
  getQuickAddViewportPrefetchObserver()?.observe(component);
}

/**
 * @param {QuickAddComponent} component
 */
function unobserveQuickAddViewportPrefetch(component) {
  getQuickAddViewportPrefetchObserver()?.unobserve(component);
}

/**
 * @param {import('./slideshow').Slideshow} slideshow
 * @param {Element[]} slides
 */
function resetQuickAddModalSlideshow(slideshow, slides) {
  slideshow.setAttribute('initial-slide', '0');

  slides.forEach((slide, index) => {
    slide.removeAttribute('reveal');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
  });

  try {
    slideshow.current = 0;
  } catch (error) {
    // Some custom elements expose current as read-only before hydration.
  }

  const scroller = slideshow.refs?.scroller || slideshow.querySelector('slideshow-slides');
  const forceFirstSlide = () => {
    if (!(scroller instanceof HTMLElement)) return;
    scroller.scrollLeft = 0;
    scroller.scrollTop = 0;
    scroller.scrollTo?.({ left: 0, top: 0, behavior: 'instant' });
  };

  forceFirstSlide();
  requestAnimationFrame(forceFirstSlide);
}

/**
 * @param {import('./slideshow').Slideshow} slideshow
 * @returns {Element[]}
 */
function getQuickAddModalSlides(slideshow) {
  return Array.from(slideshow.querySelectorAll('slideshow-slide')).filter(
    (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
  );
}

/**
 * @param {import('./slideshow').Slideshow} slideshow
 * @param {Element} arrows
 */
function syncQuickAddModalArrows(slideshow, arrows) {
  const slides = getQuickAddModalSlides(slideshow);
  if (slides.length <= 1) return;

  const current = Number.isFinite(slideshow.current) ? slideshow.current : 0;
  preloadQuickAddModalNeighborImages(slides, current);
  updateQuickAddModalArrowState(slideshow, arrows, slides.length);
}

/**
 * @param {import('./slideshow').Slideshow} slideshow
 * @param {Element} arrows
 */
function bindQuickAddModalArrowSync(slideshow, arrows) {
  if (!(arrows instanceof HTMLElement) || arrows.dataset.hpQuickAddArrowSync === 'true') return;

  arrows.dataset.hpQuickAddArrowSync = 'true';
  const sync = () => requestAnimationFrame(() => syncQuickAddModalArrows(slideshow, arrows));
  const scroller = slideshow.refs?.scroller || slideshow.querySelector('slideshow-slides');
  let scrollFrame = 0;

  slideshow.addEventListener(SlideshowSelectEvent.eventName, sync);
  scroller?.addEventListener(
    'scroll',
    () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        syncQuickAddModalArrows(slideshow, arrows);
      });
    },
    { passive: true }
  );
}

function wireQuickAddModalMediaArrows({ reset = false } = {}) {
  const modalContent = document.getElementById('quick-add-modal-content');
  const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
    modalContent?.querySelector('.product-information__media slideshow-component')
  );
  const container = slideshow?.querySelector('slideshow-container');
  const slides = slideshow ? getQuickAddModalSlides(slideshow) : [];

  if (!slideshow || !container || slides.length <= 1) return;

  slideshow.removeAttribute('infinite');
  let pendingSelection = Promise.resolve();

  if (reset) {
    resetQuickAddModalSlideshow(slideshow, slides);
    pendingSelection = Promise.resolve(slideshow.select(0, undefined, { animate: false }));
  }

  const current = Number.isFinite(slideshow.current) ? slideshow.current : 0;
  preloadQuickAddModalNeighborImages(slides, current);

  const existingArrows = container.querySelector('.quick-add-modal__media-arrows');
  if (existingArrows) {
    bindQuickAddModalArrowSync(slideshow, existingArrows);
    updateQuickAddModalArrowState(slideshow, existingArrows, slides.length);
    pendingSelection.then(() => updateQuickAddModalArrowState(slideshow, existingArrows, slides.length));
    return;
  }

  const arrows = document.createElement('div');
  arrows.className = 'quick-add-modal__media-arrows';
  arrows.innerHTML = `
    <button class="quick-add-modal__media-arrow quick-add-modal__media-arrow--prev" type="button" aria-label="Previous image">
      <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"></path></svg>
    </button>
    <button class="quick-add-modal__media-arrow quick-add-modal__media-arrow--next" type="button" aria-label="Next image">
      <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m9 6 6 6-6 6"></path></svg>
    </button>
  `;

  const selectClampedSlide = (offset, event) => {
    const current = Number.isFinite(slideshow.current) ? slideshow.current : 0;
    const nextIndex = Math.max(0, Math.min(slides.length - 1, current + offset));
    if (nextIndex === current) return;

    preloadQuickAddModalNeighborImages(slides, nextIndex);
    slideshow.select(nextIndex, event, { animate: false });
    updateQuickAddModalArrowState(slideshow, arrows, slides.length);
    requestAnimationFrame(() => updateQuickAddModalArrowState(slideshow, arrows, slides.length));
  };

  arrows.querySelector('.quick-add-modal__media-arrow--prev')?.addEventListener('click', (event) => {
    selectClampedSlide(-1, event);
  });

  arrows.querySelector('.quick-add-modal__media-arrow--next')?.addEventListener('click', (event) => {
    selectClampedSlide(1, event);
  });

  container.append(arrows);
  bindQuickAddModalArrowSync(slideshow, arrows);
  updateQuickAddModalArrowState(slideshow, arrows, slides.length);
  pendingSelection.then(() => updateQuickAddModalArrowState(slideshow, arrows, slides.length));
}

/**
 * @param {string} mediaId
 * @param {Event} [event]
 */
function selectQuickAddModalMedia(mediaId, event) {
  if (!mediaId) return false;

  const modalContent = document.getElementById('quick-add-modal-content');
  const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
    modalContent?.querySelector('.product-information__media slideshow-component')
  );
  const slides = slideshow ? Array.from(slideshow.querySelectorAll('slideshow-slide')) : [];
  const container = slideshow?.querySelector('slideshow-container');
  const arrows = container?.querySelector('.quick-add-modal__media-arrows');

  if (!slideshow || slides.length <= 1) return false;

  const index = slides.findIndex((slide) => {
    const slideMediaId = slide.getAttribute('slide-id') || slide.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    return slideMediaId === String(mediaId);
  });

  if (index < 0) return false;

  preloadQuickAddModalNeighborImages(slides, index);
  Promise.resolve(slideshow.select(index, event, { animate: false })).then(() => {
    if (arrows) updateQuickAddModalArrowState(slideshow, arrows, slides.length);
  });
  if (arrows) updateQuickAddModalArrowState(slideshow, arrows, slides.length);
  return true;
}

/**
 * @param {EventTarget | null} target
 */
function getQuickAddChangedVariantOption(target) {
  if (target instanceof HTMLSelectElement) {
    return target.options[target.selectedIndex] || null;
  }

  if (target instanceof HTMLInputElement) {
    return target;
  }

  return null;
}

function syncQuickAddModalSelectedVariantMedia() {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return false;

  const storedMediaId = modalContent.dataset.hpSelectedMediaId;
  if (storedMediaId && selectQuickAddModalMedia(storedMediaId)) return true;

  const selectedOption = modalContent.querySelector(
    'input:checked[data-option-media-id], select option:checked[data-option-media-id]'
  );
  const mediaId = selectedOption instanceof HTMLElement ? selectedOption.dataset.optionMediaId : '';

  return mediaId ? selectQuickAddModalMedia(mediaId) : false;
}

function wireQuickAddModalVariantMedia() {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent || modalContent.dataset.hpVariantMediaReady === 'true') return;

  modalContent.dataset.hpVariantMediaReady = 'true';
  modalContent.addEventListener('change', (event) => {
    const option = getQuickAddChangedVariantOption(event.target);
    const mediaId = option?.dataset.optionMediaId;
    if (!mediaId) return;

    modalContent.dataset.hpSelectedMediaId = mediaId;
    selectQuickAddModalMedia(mediaId, event);
  });
}

function setupQuickAddResponsiveSwitch() {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;

  let switcher = modalContent.querySelector('[data-hp-quick-view-switch]');

  if (!(switcher instanceof HTMLElement)) {
    switcher = document.createElement('div');
    switcher.className = 'hp-quick-view-switch';
    switcher.dataset.hpQuickViewSwitch = 'true';
    switcher.setAttribute('role', 'tablist');
    switcher.setAttribute('aria-label', 'Product quick view');
    switcher.innerHTML = `
      <button type="button" data-hp-quick-view-tab="images" role="tab">Images</button>
      <button type="button" data-hp-quick-view-tab="details" role="tab">Details</button>
    `;
    modalContent.prepend(switcher);
  }

  const setView = (view) => {
    const activeView = view === 'images' ? 'images' : 'details';
    modalContent.dataset.hpQuickView = activeView;
    modalContent.dataset.hpQuickViewPreference = activeView;

    switcher.querySelectorAll('[data-hp-quick-view-tab]').forEach((button) => {
      const isActive = button.getAttribute('data-hp-quick-view-tab') === activeView;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.setAttribute('aria-pressed', String(isActive));
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    applyQuickAddResponsiveViewState(modalContent);
    modalContent.scrollTop = 0;
  };

  if (switcher.dataset.hpSwitchReady !== 'true') {
    switcher.dataset.hpSwitchReady = 'true';
    switcher.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-hp-quick-view-tab]') : null;
      if (!(button instanceof HTMLButtonElement)) return;

      event.preventDefault();
      event.stopPropagation();
      setView(button.getAttribute('data-hp-quick-view-tab') || 'details');
    });
  }

  setView(modalContent.dataset.hpQuickViewPreference === 'images' ? 'images' : 'details');
}

/**
 * @param {HTMLElement | null | undefined} element
 * @param {boolean} visible
 */
function setQuickAddViewElementVisibility(element, visible) {
  if (!(element instanceof HTMLElement)) return;

  element.hidden = !visible;
  element.setAttribute('aria-hidden', String(!visible));

  if (visible) {
    element.style.removeProperty('display');
    element.style.removeProperty('height');
    element.style.removeProperty('min-height');
    element.style.removeProperty('margin');
    element.style.removeProperty('padding');
    element.style.removeProperty('overflow');
    return;
  }

  element.style.setProperty('display', 'none', 'important');
  element.style.setProperty('height', '0', 'important');
  element.style.setProperty('min-height', '0', 'important');
  element.style.setProperty('margin', '0', 'important');
  element.style.setProperty('padding', '0', 'important');
  element.style.setProperty('overflow', 'hidden', 'important');
}

/**
 * @param {HTMLElement} modalContent
 */
function applyQuickAddResponsiveViewState(modalContent) {
  const activeView = modalContent.dataset.hpQuickView === 'images' ? 'images' : 'details';
  const media = /** @type {HTMLElement | null} */ (modalContent.querySelector('.product-information__media'));
  const productDetails = /** @type {HTMLElement | null} */ (modalContent.querySelector('.product-details'));
  const removedControls = modalContent.querySelectorAll(
    ':scope > .product-header, :scope > variant-picker, :scope > .variant-picker, :scope > product-form-component'
  );

  if (!isMobileBreakpoint()) {
    [media, productDetails, ...removedControls].forEach((element) => {
      setQuickAddViewElementVisibility(/** @type {HTMLElement | null | undefined} */ (element), true);
    });
    return;
  }

  setQuickAddViewElementVisibility(media, activeView === 'images');
  setQuickAddViewElementVisibility(productDetails, activeView === 'details');
  removedControls.forEach((element) => setQuickAddViewElementVisibility(/** @type {HTMLElement} */ (element), false));
}

function normalizeQuickAddText(text) {
  return String(text || '')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function dedupeQuickAddProductDetails() {
  const modalContent = document.getElementById('quick-add-modal-content');
  const productDetails = modalContent?.querySelector('.product-details');
  const productTitle = productDetails?.querySelector('.view-product-title a')?.textContent || '';
  const normalizedProductTitle = normalizeQuickAddText(productTitle);

  if (!productDetails || !normalizedProductTitle) return;

  productDetails.querySelectorAll('[data-hp-quick-add-duplicate-title]').forEach((element) => {
    element.hidden = false;
    element.style.removeProperty('display');
    element.style.removeProperty('height');
    element.style.removeProperty('margin');
    element.style.removeProperty('padding');
    element.style.removeProperty('overflow');
    element.removeAttribute('aria-hidden');
    element.removeAttribute('data-hp-quick-add-duplicate-title');
  });

  const candidates = productDetails.querySelectorAll(
    '.text-block, .rte, rte-formatter, [class*="product-description"]'
  );

  candidates.forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    if (element.closest('.view-product-title, product-price, variant-picker, product-form-component, .buy-buttons-block')) return;
    if (normalizeQuickAddText(element.textContent) !== normalizedProductTitle) return;

    element.hidden = true;
    element.setAttribute('aria-hidden', 'true');
    element.style.setProperty('display', 'none', 'important');
    element.style.setProperty('height', '0', 'important');
    element.style.setProperty('margin', '0', 'important');
    element.style.setProperty('padding', '0', 'important');
    element.style.setProperty('overflow', 'hidden', 'important');
    element.dataset.hpQuickAddDuplicateTitle = 'true';
  });
}

function syncQuickAddProductDescription(modalContent) {
  if (!(modalContent instanceof HTMLElement)) return;

  const productDetails = modalContent.querySelector('.product-details');
  if (!(productDetails instanceof HTMLElement)) return;

  const normalizedProductTitle = normalizeQuickAddText(
    productDetails.querySelector('.view-product-title a')?.textContent || ''
  );

  productDetails.querySelectorAll('.hp-quick-product-description').forEach((element) => {
    element.classList.remove('hp-quick-product-description');
  });

  const candidates = Array.from(
    productDetails.querySelectorAll('.product-description, [class*="product-description"], .text-block.rte, rte-formatter.rte')
  )
    .filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.dataset.hpQuickAddDuplicateTitle === 'true') return false;
      if (element.closest('.view-product-title, product-price, variant-picker, product-form-component, .buy-buttons-block')) {
        return false;
      }

      const normalizedText = normalizeQuickAddText(element.textContent);
      return Boolean(normalizedText) && normalizedText !== normalizedProductTitle;
    })
    .map((element) => {
      const isExplicitDescription = element.matches('.product-description, [class*="product-description"]');
      const isRte = element.matches('.text-block.rte, rte-formatter.rte');
      return {
        element,
        score:
          (isExplicitDescription ? 100 : 0) +
          (isRte ? 5 : 0) +
          Math.min(normalizeQuickAddText(element.textContent).length / 20, 20),
      };
    })
    .sort((a, b) => b.score - a.score);

  const description = candidates[0]?.element;
  if (!(description instanceof HTMLElement)) return;

  description.classList.add('hp-quick-product-description');
  description.hidden = false;
  description.removeAttribute('aria-hidden');
  description.style.removeProperty('display');
  description.style.removeProperty('height');
  description.style.removeProperty('min-height');
  description.style.removeProperty('margin');
  description.style.removeProperty('padding');
  description.style.removeProperty('overflow');
}

function setupQuickAddProductPageButton() {
  const modalContent = document.getElementById('quick-add-modal-content');
  const productDetails = modalContent?.querySelector('.product-details');
  const titleLink = /** @type {HTMLAnchorElement | null} */ (productDetails?.querySelector('.view-product-title a'));

  if (!productDetails || !titleLink) return;

  const href = titleLink.dataset.hpProductHref || titleLink.getAttribute('href') || titleLink.href;
  if (!href) return;

  titleLink.dataset.hpProductHref = href;
  titleLink.setAttribute('aria-disabled', 'true');
  titleLink.setAttribute('tabindex', '-1');
  titleLink.removeAttribute('href');

  if (titleLink.dataset.hpTitleClickDisabled !== 'true') {
    titleLink.dataset.hpTitleClickDisabled = 'true';
    titleLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  let productPageButton = /** @type {HTMLAnchorElement | null} */ (
    productDetails.querySelector('.hp-quick-product-page-button')
  );
  const productPageButtonTarget =
    productDetails.querySelector(':scope > .group-block .group-block-content') || productDetails;

  if (!productPageButton) {
    productPageButton = document.createElement('a');
    productPageButton.className = 'hp-quick-product-page-button';
    productPageButton.textContent = 'View product';
  }

  productPageButtonTarget.append(productPageButton);
  productPageButton.href = href;
}

/**
 * @param {Element | null} label
 */
function getQuickAddVariantLabelText(label) {
  if (!label) return '';

  const clone = label.cloneNode(true);
  if (clone instanceof HTMLElement) {
    clone.querySelectorAll('.variant-option__swatch-value, svg, input').forEach((element) => element.remove());
  }

  return String(clone.textContent || '').replace(/\s+/g, ' ').trim();
}

function setupQuickAddVariantDropdowns() {
  if (!isMobileBreakpoint()) return;

  const modalContent = document.getElementById('quick-add-modal-content');
  const variantPicker = /** @type {HTMLElement | null} */ (modalContent?.querySelector('variant-picker'));
  if (!variantPicker) return;

  variantPicker.querySelectorAll('.hp-quick-variant-dropdowns').forEach((element) => element.remove());
  variantPicker.querySelectorAll('.hp-quick-variant-native-hidden').forEach((element) => {
    element.classList.remove('hp-quick-variant-native-hidden');
  });

  const optionGroups = Array.from(variantPicker.querySelectorAll('fieldset.variant-option--buttons'));
  if (!optionGroups.length) return;

  const dropdowns = document.createElement('div');
  dropdowns.className = 'hp-quick-variant-dropdowns';

  optionGroups.forEach((group, groupIndex) => {
    if (!(group instanceof HTMLFieldSetElement)) return;

    const inputs = Array.from(group.querySelectorAll('input[type="radio"]')).filter(
      (input) => input instanceof HTMLInputElement
    );
    if (!inputs.length) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'hp-quick-variant-dropdown';

    const selectId = `hp-quick-variant-${variantPicker.dataset.productId || 'product'}-${groupIndex}`;
    const label = document.createElement('label');
    label.className = 'hp-quick-variant-dropdown__label';
    label.htmlFor = selectId;
    label.textContent = getQuickAddVariantLabelText(group.querySelector('legend')) || `Option ${groupIndex + 1}`;

    const select = document.createElement('select');
    select.id = selectId;
    select.className = 'hp-quick-variant-dropdown__select';
    select.setAttribute('aria-label', label.textContent);

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const inputLabel = input.closest('label');
      const optionText =
        getQuickAddVariantLabelText(inputLabel?.querySelector('[data-key="variant-option-text"]')) ||
        getQuickAddVariantLabelText(inputLabel) ||
        input.value;
      const option = document.createElement('option');
      const unavailable = input.dataset.optionAvailable === 'false' || input.getAttribute('aria-disabled') === 'true';

      option.value = input.dataset.optionValueId || input.value;
      option.textContent = unavailable ? `${optionText} - Unavailable` : optionText;
      option.selected = input.checked;
      option.dataset.inputIndex = input.dataset.inputIndex || '';
      option.dataset.optionValueId = input.dataset.optionValueId || '';
      option.dataset.variantId = input.dataset.variantId || '';
      option.dataset.connectedProductUrl = input.dataset.connectedProductUrl || '';
      option.dataset.optionMediaId = input.dataset.optionMediaId || '';
      select.append(option);
    });

    select.addEventListener('change', (event) => {
      event.stopPropagation();

      const selectedOption = select.options[select.selectedIndex];
      const nextInput = inputs.find((input) => input.dataset.optionValueId === selectedOption?.value);
      if (!(nextInput instanceof HTMLInputElement)) return;

      nextInput.click();
      requestAnimationFrame(setupQuickAddVariantDropdowns);
    });

    wrapper.append(label, select);
    dropdowns.append(wrapper);
    group.classList.add('hp-quick-variant-native-hidden');
  });

  if (!dropdowns.children.length) return;

  const variantForm = variantPicker.querySelector('.variant-picker__form') || variantPicker;
  variantForm.insertBefore(dropdowns, optionGroups[0] || variantForm.firstChild);
}

/**
 * @param {Element[]} slides
 * @param {number} current
 */
function preloadQuickAddModalNeighborImages(slides, current = 0) {
  [current - 1, current, current + 1].forEach((index) => {
    const slide = slides[index];
    const image = /** @type {HTMLImageElement | null} */ (slide?.querySelector('img'));
    const src = image?.currentSrc || image?.getAttribute('src');

    if (!image || !src) return;

    image.loading = 'eager';
    image.fetchPriority = 'high';

    const preload = new Image();
    preload.decoding = 'async';
    preload.src = src;
  });
}

const quickAddCheckoutStates = new WeakMap();
let quickAddCheckoutLifecycleReady = false;
let quickAddCheckoutGeneration = 0;
let quickAddCheckoutPageActive = true;

function waitForQuickAddReceipt(promise, onTimeout) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error('Your cart is still updating. Please wait a moment and retry checkout.'));
      onTimeout?.();
    }, 15000);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function quickAddLineIdentity(variantId, properties, sellingPlan) {
  if (!/^\d+$/.test(String(variantId || ''))) return null;
  if (properties != null && (typeof properties !== 'object' || Array.isArray(properties))) return null;
  if (sellingPlan && !/^\d+$/.test(String(sellingPlan))) return null;
  const entries = Object.entries(properties || {}).filter(([, value]) => value !== '' && value != null);
  if (entries.some(([, value]) => typeof value !== 'string')) return null;
  entries.sort(([left], [right]) => left.localeCompare(right));
  return JSON.stringify([String(variantId), String(sellingPlan || ''), entries]);
}

function quickAddCartLineIdentity(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)
    || (item.selling_plan_allocation && !item.selling_plan_allocation.selling_plan?.id)) return null;
  return quickAddLineIdentity(item.variant_id || item.id, item.properties,
    item.selling_plan_allocation?.selling_plan?.id || item.selling_plan);
}

function quickAddSelection(formData) {
  const properties = {};
  for (const [name, value] of formData.entries()) {
    const property = /^properties\[(.*)\]$/.exec(name);
    if (property) properties[property[1]] = value;
  }
  const variantId = String(formData.get('id') || '');
  const sellingPlan = String(formData.get('selling_plan') || '');
  const quantity = Number(formData.get('quantity') || 1);
  if (!/^\d+$/.test(variantId) || !Number.isSafeInteger(quantity) || quantity < 1) {
    throw new Error('Please check the selected product and quantity before checkout.');
  }
  return { variantId, sellingPlan, quantity, identity: quickAddLineIdentity(variantId, properties, sellingPlan) };
}

async function verifyQuickAddReceipt(receipt, isCurrent) {
  const mutations = window.HalfpipedCartMutations;
  if (typeof mutations?.whenIdle !== 'function' || typeof mutations.getRevision !== 'function'
    || typeof mutations.isPending !== 'function') {
    throw new Error('Cart verification is still loading. Please refresh the page and try again.');
  }
  for (let attempt = 0; attempt < 3; attempt += 1) {
    await mutations.whenIdle({ recoverFailureThroughRevision: receipt.mutationRevision });
    if (!isCurrent()) return null;
    if (mutations.isPending()) continue;
    const revision = mutations.getRevision();
    const root = String(window.Shopify?.routes?.root || '/');
    const controller = new AbortController();
    let cart;
    try {
      cart = await waitForQuickAddReceipt((async () => {
        const response = await fetch(`${root.endsWith('/') ? root : `${root}/`}cart.js`, {
          headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        });
        if (!response.ok) throw new Error('Could not verify your cart. Please try checkout again.');
        const payload = await response.json();
        if (!Array.isArray(payload?.items)) throw new Error('Could not verify your cart. Please try checkout again.');
        return payload;
      })(), () => controller.abort());
    } finally { controller.abort(); }
    if (!isCurrent()) return null;
    if (mutations.isPending() || revision !== mutations.getRevision()) continue;
    let quantity = 0;
    for (const item of cart.items) {
      const identity = quickAddCartLineIdentity(item);
      if (!identity || !Number.isSafeInteger(item.quantity) || item.quantity < 1) {
        throw new Error('Could not verify your cart. Please try checkout again.');
      }
      if (identity === receipt.identity) quantity += item.quantity;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart, data: { source: 'quick-add-checkout-verification', cartConfirmed: true, cartRevision: revision } },
    }));
    if (!isCurrent()) return null;
    if (mutations.isPending() || revision !== mutations.getRevision()) continue;
    // The header renders cart:refresh synchronously. Clear an old POST failure
    // only after that canonical cart has reached the existing cart surfaces.
    mutations.acknowledgeCart?.(revision);
    if (!receipt.identity || (!receipt.confirmed && !receipt.rejected && quantity < receipt.quantity)) {
      // A lost POST response does not prove the add failed. Never repeat that
      // write merely because a later read has not found its item yet.
      throw new Error('Could not confirm whether this item was added. Please review your cart before trying again.');
    }
    if (quantity >= receipt.quantity) receipt.confirmed = true;
    return { missing: Math.max(0, receipt.quantity - quantity), revision, mutations };
  }
  throw new Error('Your cart is still changing. Please wait and retry checkout.');
}

function restoreQuickAddCheckoutButton(button, state) {
  button.disabled = false;
  button.classList.remove('is-loading');
  if (state.addedFormSignature && state.addedFormSignature === state.formSignature) button.textContent = 'Retry checkout';
  else button.innerHTML = state.originalButtonHtml;
}

function ensureQuickAddCheckoutLifecycle() {
  if (quickAddCheckoutLifecycleReady) return;
  quickAddCheckoutLifecycleReady = true;
  window.addEventListener('pagehide', () => {
    quickAddCheckoutGeneration += 1;
    quickAddCheckoutPageActive = false;
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted && quickAddCheckoutPageActive) return;
    quickAddCheckoutGeneration += 1;
    quickAddCheckoutPageActive = true;
    // Query current buttons instead of keeping old modal DOM alive in a Set.
    document.querySelectorAll('[data-hp-instant-checkout]').forEach(button => {
      const state = quickAddCheckoutStates.get(button);
      if (state) restoreQuickAddCheckoutButton(button, state);
    });
  });
}

function wireQuickAddInstantCheckout() {
  ensureQuickAddCheckoutLifecycle();
  const modalContent = document.getElementById('quick-add-modal-content');
  const form = /** @type {HTMLFormElement | null} */ (
    modalContent?.querySelector('product-form-component form[action*="/cart/add"]')
  );
  if (!form) return;

  const productFormComponent = form.closest('product-form-component');
  const acceleratedCheckout = form.querySelector('.accelerated-checkout-block');
  const existingButton = form.querySelector('[data-hp-instant-checkout]');

  if (acceleratedCheckout instanceof HTMLElement) {
    acceleratedCheckout.hidden = true;
    acceleratedCheckout.setAttribute('aria-hidden', 'true');
  }

  if (existingButton) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'hp-quick-checkout-button';
  button.dataset.hpInstantCheckout = 'true';
  button.innerHTML = `
    <span class="hp-quick-checkout-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M13 2 4 13h7l-1 9 9-12h-7l1-8Z"></path>
      </svg>
    </span>
    <span class="hp-quick-checkout-text">
      <span>Instant</span>
      <span>checkout</span>
    </span>
  `;

  const addToCartComponent = form.querySelector('add-to-cart-component');
  const insertBefore = acceleratedCheckout || addToCartComponent?.nextSibling || null;

  if (insertBefore) {
    insertBefore.parentNode?.insertBefore(button, insertBefore);
  } else {
    form.append(button);
  }

  const state = { addedFormSignature: '', formSignature: '', originalButtonHtml: button.innerHTML, attempt: 0, pendingAdd: null, receipts: new Map() };
  quickAddCheckoutStates.set(button, state);
  button.addEventListener('click', async () => {
    if (button.disabled || !quickAddCheckoutPageActive) return;
    if (!form.reportValidity()) return;

    const formData = new FormData(form);
    const formSignature = JSON.stringify([...formData.entries()].map(([key, value]) => [key,
      typeof value === 'string' ? value : [value.name, value.size, value.type, value.lastModified],
    ]));
    state.formSignature = formSignature;
    const attempt = ++state.attempt;
    const generation = quickAddCheckoutGeneration;
    const isCurrent = () => quickAddCheckoutPageActive && generation === quickAddCheckoutGeneration
      && attempt === state.attempt && button.isConnected;

    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Checking out...';

    try {
      // The quick-add button must use the same cart checkout decision as the
      // drawer. Direct navigation can otherwise drop a board's promotion.
      let checkoutMode = document.documentElement?.getAttribute('data-hp-board-checkout-mode') || 'off';
      const checkoutConfig = document.getElementById('hp-board-checkout-config');
      if (checkoutConfig) {
        try {
          checkoutMode = String(JSON.parse(checkoutConfig.textContent || '{}').mode || checkoutMode).trim().toLowerCase();
        } catch (error) {
          throw new Error('Checkout is not ready. Please refresh the page and try again.');
        }
      }
      const boardCheckout = window.HalfpipedBoardCheckout;
      if (checkoutMode === 'create' && (boardCheckout?.config?.mode !== 'create'
        || typeof boardCheckout.beginCheckout !== 'function')) {
        throw new Error('Checkout is still loading. Please wait a moment and try again.');
      }
      if (state.pendingAdd) {
        await waitForQuickAddReceipt(state.pendingAdd);
        if (!isCurrent()) return;
      }
      const selection = quickAddSelection(formData);
      let quantityToAdd = selection.quantity;
      const priorReceipt = state.receipts.get(formSignature);
      if (priorReceipt) {
        let verified;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          verified = await verifyQuickAddReceipt(priorReceipt, isCurrent);
          if (!isCurrent() || !verified) return;
          if (!verified.mutations.isPending() && verified.revision === verified.mutations.getRevision()) break;
          verified = null;
        }
        if (!verified) throw new Error('Your cart is still changing. Please wait and retry checkout.');
        quantityToAdd = verified.missing;
        if (!quantityToAdd) state.addedFormSignature = formSignature;
      }
      if (quantityToAdd > 0) {
        formData.set('quantity', String(quantityToAdd));
        // Preserve an uncertain receipt before sending: a rejected fetch can
        // still represent a successful server-side add.
        const receipt = { signature: formSignature, identity: selection.identity, quantity: selection.quantity, confirmed: false, rejected: false, mutationRevision: null };
        state.receipts.set(formSignature, receipt);
        const add = (async () => {
          const request = fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
          receipt.mutationRevision = window.HalfpipedCartMutations?.getRevision?.();
          const response = await request;
          const payload = await response.json();
          if (!response.ok || payload.status) {
            // A structured validation rejection can be retried after a fresh
            // read, adding only what is absent even if Shopify partly added it.
            receipt.rejected = [400, 422].includes(Number(response.status || payload.status))
              && Boolean(payload.description || payload.message);
            throw new Error(String(payload.description || payload.message || 'Could not start checkout'));
          }
          const candidates = Array.isArray(payload.items) ? payload.items : [payload];
          const item = candidates.find(candidate => String(candidate.variant_id || candidate.id) === selection.variantId
            && String(candidate.selling_plan_allocation?.selling_plan?.id || candidate.selling_plan || '') === selection.sellingPlan
            && (!selection.identity || quickAddCartLineIdentity(candidate) === selection.identity));
          const identity = item && quickAddCartLineIdentity(item);
          if (!identity || (selection.identity && identity !== selection.identity)
            || !Number.isSafeInteger(item.quantity) || item.quantity < quantityToAdd) {
            throw new Error('Could not confirm whether this item was added. Please review your cart before trying again.');
          }
          // Keep the receipt even if navigation superseded the initiating click.
          receipt.identity = identity;
          receipt.confirmed = true;
          state.addedFormSignature = formSignature;
        })();
        state.pendingAdd = add;
        const clearPendingAdd = () => { if (state.pendingAdd === add) state.pendingAdd = null; };
        add.then(clearPendingAdd, clearPendingAdd);
        await waitForQuickAddReceipt(add);
        if (!isCurrent()) return;
      }

      if (checkoutMode === 'create') {
        const result = await boardCheckout.beginCheckout({
          type: 'link', href: new URL(boardCheckout.getCheckoutPath(), window.location.origin).toString(),
        });
        if (!isCurrent()) return;
        if (!result) throw new Error(boardCheckout.lastCheckoutError || 'Could not prepare checkout. Please try again.');
      } else {
        await window.HalfpipedCartMutations?.whenIdle?.();
        if (!isCurrent()) return;
        const root = String(window.Shopify?.routes?.root || '/');
        window.location.href = `${root.endsWith('/') ? root : `${root}/`}checkout`;
      }
    } catch (error) {
      if (!isCurrent()) return;
      const message = error instanceof Error ? error.message : 'Could not start checkout';
      showQuickAddCheckoutError(productFormComponent, message);
      restoreQuickAddCheckoutButton(button, state);
    }
  });
}

function refreshQuickAddModalQuantityLimits() {
  const modalContent = document.getElementById('quick-add-modal-content');
  const productForm = modalContent?.querySelector('product-form-component');

  productForm?.refreshCartQuantity?.();
}

/**
 * @param {Element | null} productFormComponent
 * @param {string} message
 */
function showQuickAddCheckoutError(productFormComponent, message) {
  const errorElement = productFormComponent?.querySelector('[ref="addToCartTextError"], .product-form-text__error');
  const liveRegion = productFormComponent?.querySelector('[ref="liveRegion"]');

  if (errorElement instanceof HTMLElement) {
    errorElement.classList.remove('hidden');
    const textNode = errorElement.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorElement.appendChild(document.createTextNode(message));
    }
  }

  if (liveRegion) {
    liveRegion.textContent = message;
  }
}

/**
 * @param {import('./slideshow').Slideshow} slideshow
 * @param {Element} arrows
 * @param {number} slideCount
 */
function updateQuickAddModalArrowState(slideshow, arrows, slideCount) {
  const rawCurrent = Number.isFinite(slideshow.current) ? slideshow.current : 0;
  const current = Math.max(0, Math.min(slideCount - 1, rawCurrent));
  const previous = /** @type {HTMLButtonElement | null} */ (arrows.querySelector('.quick-add-modal__media-arrow--prev'));
  const next = /** @type {HTMLButtonElement | null} */ (arrows.querySelector('.quick-add-modal__media-arrow--next'));

  if (previous) previous.disabled = current <= 0;
  if (next) next.disabled = current >= slideCount - 1;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = quickAddContentCache;
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = quickAddPendingContent;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener('pointerenter', this.#prefetchProductPage);
    this.addEventListener('focusin', this.#prefetchProductPage);
    this.closest('product-card')?.addEventListener('pointerenter', this.#prefetchProductPage);
    this.closest('product-card')?.addEventListener('focusin', this.#prefetchProductPage);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    observeQuickAddViewportPrefetch(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.removeEventListener('focusin', this.#prefetchProductPage);
    this.closest('product-card')?.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.closest('product-card')?.removeEventListener('focusin', this.#prefetchProductPage);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    unobserveQuickAddViewportPrefetch(this);
  }

  prefetchProductPage() {
    if (!this.isConnected) return;

    this.#prefetchProductPage();
  }

  /**
   * Drops in-flight preloads after a cart update while keeping loaded product shells available.
   */
  #handleCartUpdate = () => {
    this.#pendingContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Renders a cached/fetched product into the quick-add modal.
   * @param {Element} productGrid
   * @param {() => boolean} isCurrentRequest
   */
  async #renderQuickAddProductGrid(productGrid, isCurrentRequest) {
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    if (!isCurrentRequest()) return false;

    this.#updateVariantPicker(productGrid);
    wireQuickAddModalMediaArrows({ reset: true });
    wireQuickAddModalVariantMedia();
    wireQuickAddInstantCheckout();
    setupQuickAddVariantDropdowns();
    syncQuickAddProductDescription(document.getElementById('quick-add-modal-content'));
    setupQuickAddProductPageButton();
    refreshQuickAddModalQuantityLimits();
    return true;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const requestedView =
      event.target instanceof Element && event.target.closest('.quick-add__button--collection-view') ? 'images' : 'details';

    await this.#openQuickAddProduct(requestedView);
  };

  /**
   * Opens and fills the quick-add modal.
   * @param {'images' | 'details'} requestedView
   */
  async #openQuickAddProduct(requestedView) {
    const currentUrl = this.productPageUrl;
    const requestId = `${Date.now()}-${++quickAddRequestCounter}`;
    const modalContent = document.getElementById('quick-add-modal-content');
    const isCurrentRequest = () => modalContent?.dataset.hpQuickAddRequest === requestId;

    if (modalContent) {
      modalContent.dataset.hpQuickAddRequest = requestId;
      modalContent.dataset.hpQuickViewPreference = requestedView;
      modalContent.dataset.hpQuickView = requestedView;
    }

    let productGrid = this.#cachedContent.get(currentUrl);
    const shouldKeepOpenAfterLoading = !productGrid;
    const wasModalOpen = this.#isQuickAddModalOpen();
    const shouldDelayCollectionPopup =
      requestedView === 'images' && !wasModalOpen && isQuickAddMobileOrTabletBreakpoint();

    if (productGrid && !wasModalOpen) {
      if (!(await this.#renderQuickAddProductGrid(productGrid, isCurrentRequest))) return;
      this.#openQuickAddModal();
      return;
    }

    if (!wasModalOpen && !shouldDelayCollectionPopup) {
      this.#showLoadingModal();
      this.#openQuickAddModal();
    }

    if (!productGrid) {
      if (wasModalOpen) this.#showLoadingModal();
      productGrid = (await this.#getProductGrid(currentUrl)) ?? undefined;

      if (!isCurrentRequest() || (!this.#isQuickAddModalOpen() && !shouldDelayCollectionPopup)) return;
    }

    if (!isCurrentRequest()) return;

    if (productGrid) {
      if (!(await this.#renderQuickAddProductGrid(productGrid, isCurrentRequest))) return;
    } else {
      this.#showErrorModal();
    }

    if (shouldDelayCollectionPopup || !shouldKeepOpenAfterLoading || this.#isQuickAddModalOpen()) {
      if (!isCurrentRequest()) return;
      this.#openQuickAddModal();
    }
  }

  #prefetchProductPage = () => {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl) || this.#pendingContent.has(currentUrl)) return;

    this.#getProductGrid(currentUrl).catch(() => null);
  };

  /**
   * Gets cached quick-add content or fetches and caches it.
   * @param {string} currentUrl
   * @returns {Promise<Element | null>}
   */
  #getProductGrid(currentUrl) {
    if (!currentUrl) return Promise.resolve(null);

    const cached = this.#cachedContent.get(currentUrl);
    if (cached) return Promise.resolve(cached);

    const pending = this.#pendingContent.get(currentUrl);
    if (pending) return pending;

    const loadProductGrid = async (url) => {
      const html = await this.fetchProductPage(url);
      if (!html) return null;

      let gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement && html.body?.firstElementChild) {
        gridElement = html.body.querySelector('[data-product-grid-content]');
      }

      if (!gridElement) return null;

      return /** @type {Element} */ (gridElement.cloneNode(true));
    };

    const request = loadProductGrid(currentUrl)
      .then(async (productGrid) => {
        if (!productGrid) {
          const fallbackUrl = new URL(currentUrl, window.location.origin);
          if (fallbackUrl.searchParams.has('variant')) {
            fallbackUrl.searchParams.delete('variant');
            productGrid = await loadProductGrid(fallbackUrl.toString());
          }
        }

        if (!productGrid) {
          const html = await this.fetchProductPage(currentUrl);
          const gridElement = html?.querySelector('[data-product-grid-content]');
          if (!gridElement) return null;
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        }

        this.#cachedContent.set(currentUrl, productGrid);
        return productGrid;
      })
      .catch(() => null)
      .finally(() => this.#pendingContent.delete(currentUrl));

    this.#pendingContent.set(currentUrl, request);
    return request;
  }

  #showLoadingModal() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.innerHTML = `
      <div class="quick-add-modal__loading" role="status" aria-live="polite">
        <span class="quick-add-modal__loading-panel" aria-hidden="true"></span>
        <span class="quick-add-modal__loading-panel quick-add-modal__loading-panel--details">
          Loading product
        </span>
      </div>
    `;
  }

  #showErrorModal() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.innerHTML = `
      <div class="quick-add-modal__loading" role="status">
        <span>Product details could not load. Open the product page to continue.</span>
      </div>
    `;
  }

  #wireModalMediaArrows() {
    wireQuickAddModalMediaArrows({ reset: true });
    wireQuickAddModalVariantMedia();
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
    this.#resetScroll();
    requestAnimationFrame(() => this.#resetScroll());
  };

  #isQuickAddModalOpen() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    const dialog = dialogComponent instanceof QuickAddDialog ? dialogComponent.refs?.dialog : null;
    return Boolean((dialog?.open || dialog?.hasAttribute('open')) && !dialog.classList.contains('dialog-closing'));
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    cancelCurrentQuickAddRequest();
    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const fullPageUrl = new URL(productPageUrl, window.location.origin);
      const sectionUrl = new URL(fullPageUrl);
      sectionUrl.searchParams.set('section_id', QUICK_ADD_PRODUCT_SECTION_ID);
      const requestUrls = [...new Set([sectionUrl.toString(), fullPageUrl.toString()])];
      let fallbackHtml = null;
      let lastError = null;

      for (const requestUrl of requestUrls) {
        try {
          const response = await fetch(requestUrl, {
            signal: this.#abortController.signal,
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
          }

          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          if (html.querySelector('[data-product-grid-content]')) {
            return html;
          }

          fallbackHtml ??= html;
        } catch (error) {
          if (error.name === 'AbortError') return null;
          lastError = error;
        }
      }

      if (fallbackHtml) return fallbackHtml;
      if (lastError) throw lastError;
      return null;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    modalContent.dataset.hpSelectedMediaId = '';

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');

      if (productDetails) {
        productDetails.dataset.hpQuickAddDetails = 'true';
        productGrid.appendChild(productDetails);
      }

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    wireQuickAddModalVariantMedia();
    wireQuickAddInstantCheckout();
    setupQuickAddResponsiveSwitch();
    dedupeQuickAddProductDetails();
    syncQuickAddProductDescription(modalContent);
    setupQuickAddVariantDropdowns();
    setupQuickAddProductPageButton();
    refreshQuickAddModalQuantityLimits();
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #previousScrollY = 0;
  #previousBodyPaddingRight = '';
  #previousBodyPosition = '';
  #previousBodyTop = '';
  #previousBodyWidth = '';
  #isClosing = false;
  /** @type {Promise<void> | null} */
  #closePromise = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target.closest('.quick-add-modal__close') : null;
        if (target) cancelCurrentQuickAddRequest();
      },
      { capture: true, signal: this.#abortController.signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') cancelCurrentQuickAddRequest();
      },
      { capture: true, signal: this.#abortController.signal }
    );
    this.refs?.dialog?.addEventListener('cancel', cancelCurrentQuickAddRequest, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  showDialog() {
    const dialog = this.refs?.dialog;
    if (!dialog || dialog.open || this.#isClosing) return;

    this.#previousScrollY = window.scrollY;
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    const currentPaddingRight = Number.parseFloat(getComputedStyle(document.body).paddingRight) || 0;
    this.#previousBodyPaddingRight = document.body.style.paddingRight;
    this.#previousBodyPosition = document.body.style.position;
    this.#previousBodyTop = document.body.style.top;
    this.#previousBodyWidth = document.body.style.width;

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    dialog.classList.remove('dialog-closing');
    dialog.style.animation = 'none';
    dialog.style.opacity = '1';
    dialog.style.pointerEvents = 'auto';
    dialog.style.transform = 'none';
    dialog.style.visibility = 'visible';

    try {
      dialog.showModal();
    } catch (error) {
      this.#restorePageScroll();
      return;
    }

    this.dispatchEvent(new DialogOpenEvent());
    this.addEventListener('click', this.#handleImmediateDialogClick);
    this.addEventListener('keydown', this.#handleImmediateDialogKeyDown);
    dialog.addEventListener('cancel', this.#handleImmediateDialogCancel);
  }

  closeDialog = async () => {
    const dialog = this.refs?.dialog;
    if (!dialog) return;
    if (this.#closePromise) return this.#closePromise;

    if (!dialog.open) {
      cancelCurrentQuickAddRequest();
      this.dispatchEvent(new DialogCloseEvent());
      return;
    }

    this.#isClosing = true;
    this.#closePromise = Promise.resolve().then(() => {
      this.removeEventListener('click', this.#handleImmediateDialogClick);
      this.removeEventListener('keydown', this.#handleImmediateDialogKeyDown);
      dialog.removeEventListener('cancel', this.#handleImmediateDialogCancel);

      cancelCurrentQuickAddRequest();
      dialog.classList.remove('dialog-closing');
      dialog.style.animation = 'none';
      dialog.style.opacity = '1';
      dialog.style.pointerEvents = 'auto';
      dialog.style.transform = 'none';
      dialog.style.visibility = 'visible';

      this.#restorePageScroll();
      dialog.close();
      dialog.style.removeProperty('animation');
      dialog.style.removeProperty('opacity');
      dialog.style.removeProperty('pointer-events');
      dialog.style.removeProperty('transform');
      dialog.style.removeProperty('visibility');

      this.#isClosing = false;
      this.#closePromise = null;
      this.dispatchEvent(new DialogCloseEvent());
    });

    return this.#closePromise;
  };

  #restorePageScroll() {
    document.body.style.width = this.#previousBodyWidth;
    document.body.style.position = this.#previousBodyPosition;
    document.body.style.top = this.#previousBodyTop;
    document.body.style.paddingRight = this.#previousBodyPaddingRight;

    try {
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    } catch (error) {
      window.scrollTo(0, this.#previousScrollY);
    }
  }

  #handleImmediateDialogClick = (event) => {
    const dialog = this.refs?.dialog;
    if (!dialog || event.target !== dialog) return;

    this.closeDialog();
  };

  #handleImmediateDialogKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    cancelCurrentQuickAddRequest();
    this.closeDialog();
  };

  #handleImmediateDialogCancel = (event) => {
    event.preventDefault();
    cancelCurrentQuickAddRequest();
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    requestAnimationFrame(() => {
      wireQuickAddModalMediaArrows({ reset: false });
      wireQuickAddModalVariantMedia();
      syncQuickAddModalSelectedVariantMedia();
      wireQuickAddInstantCheckout();
      dedupeQuickAddProductDetails();
      syncQuickAddProductDescription(this);
      setupQuickAddVariantDropdowns();
      setupQuickAddProductPageButton();
      refreshQuickAddModalQuantityLimits();
    });
  };

  #handleDialogClose = () => {
    cancelCurrentQuickAddRequest();
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
